/**
 * The Billing tab (`docs/visits-billing-and-proposal.md` §8, Boards 11, 12,
 * 15 and 16).
 *
 * The Build tab answers "what are we doing and what does it cost"; this one
 * answers **when does the money arrive**. Every visit sits on exactly one
 * plan; a plan asks *How do you bill this?*, answers in a sentence, and lists
 * every invoice it will make.
 *
 * **Nothing here computes an invoice.** `plannedInvoices()` on the server
 * writes every row and every *Comes to*; `billing-words.ts` words them. A
 * client that worked out a deposit schedule would be a second opinion about
 * when a customer gets billed, and the customer only gets one of those.
 */
import { useState } from 'react'
import {
  BILLING_TAB, planWordsContext, type QuoteTree,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { useNarrow, usePhone } from '../../lib/use-narrow.js'
import { DRAG_TYPE, PlanCard, planHasProblem } from './plan-card.js'
import { NewPlanMenu } from './billing-toolbar.js'
import type { BillingLayout } from './kind-panel.js'
import { DROP_FOR_NEW_PLAN, NO_PLANS_MATCH } from '../../builder/copy.js'

export interface BillingTabProps {
  quote: QuoteTree
  canEdit: boolean
  busy: boolean
  /** The builder's own `mutate`, so every change comes back as a whole tree. */
  onMutate: (
    path: string,
    init: { method: 'POST' | 'PATCH' | 'DELETE'; body?: unknown },
  ) => Promise<unknown>
  /** Clicking a visit goes back to Build with that card selected. */
  onOpenVisit: (visitId: string) => void
  /** The toolbar's search (step 21 item 5.1): a plan, or any visit on it, by name. */
  search?: string
  /** Folded plans, held by the page so Collapse all reaches every one. */
  collapsed?: Set<string>
  onToggle?: (planId: string) => void
}

export function BillingTab({
  quote, canEdit, busy, onMutate, onOpenVisit, search = '', collapsed, onToggle,
}: BillingTabProps) {
  const phone = usePhone()
  const portrait = useNarrow(1023)
  const layout: BillingLayout = phone ? 'phone' : portrait ? 'portrait' : 'desktop'
  /*
   * Which plan the drag is over. State rather than `:hover`, because the
   * highlight follows the drag, not the pointer.
   */
  const [over, setOver] = useState<string | null>(null)
  /* A phone has no toolbar, so its **+ New billing plan** opens the kinds as a sheet. */
  const [picking, setPicking] = useState(false)
  const needle = search.trim().toLowerCase()
  const shown = needle === ''
    ? quote.plans
    : quote.plans.filter((plan) => plan.name.toLowerCase().includes(needle)
      || quote.visits.some((visit) => visit.billingPlanId === plan.id
        && visit.name.toLowerCase().includes(needle)))

  const assign = async (planId: string, visitId: string) => {
    setOver(null)
    await onMutate(`/plans/${planId}/assign`, { method: 'POST', body: { visitId } })
  }
  // Dropping onto the dashed card makes the plan and puts the visit on it, in one gesture.
  const dropAsNewPlan = async (visitId: string) => {
    setOver(null)
    await onMutate('/plans', { method: 'POST', body: { visitId } })
  }

  return (
    <div className="flex min-w-0 flex-col gap-3.5" data-billing-tab="">
      {/*
        * Aaron (review item 5.1, then step 23): "Every visit sits on one
        * billing plan…" is gone at every width. cb-16 draws it on the phone;
        * he asked for it gone there too.
        */}

      {shown.length === 0 && (
        <p className="m-0 px-0.5 text-[13px] text-text-muted">{NO_PLANS_MATCH(search.trim())}</p>
      )}

      {shown.map((plan, index) => (
        <PlanCard
          key={plan.id}
          plan={plan}
          ctx={planWordsContext(plan, quote.visits)}
          quoteVisits={quote.visits}
          layout={layout}
          canEdit={canEdit}
          busy={busy}
          initiallyOpen={index === 0 || planHasProblem(plan)}
          dropping={over === plan.id}
          onDragOver={(on) => setOver(on ? plan.id : null)}
          onAssign={(visitId) => void assign(plan.id, visitId)}
          onUnassign={(visitId) => void onMutate(`/visits/${visitId}`, {
            method: 'PATCH', body: { billingPlanId: null },
          })}
          onOpenVisit={onOpenVisit}
          onPatch={(body) => onMutate(`/plans/${plan.id}`, { method: 'PATCH', body })}
          onDelete={() => void onMutate(`/plans/${plan.id}`, { method: 'DELETE' })}
          collapsed={collapsed ? collapsed.has(plan.id) : undefined}
          onToggle={onToggle ? () => onToggle(plan.id) : undefined}
        />
      ))}

      {/*
        * On a desktop the toolbar's **New billing plan ▾** makes a plan
        * (step 21 item 5.1), so this is only where a visit is dropped to get a
        * plan of its own. A phone has no toolbar, so there it stays a button —
        * and opens the same five kinds as a sheet, so a plan is made as the
        * kind it is meant to be on a phone too.
        */}
      {canEdit && (
        <button
          type="button"
          disabled={busy}
          onClick={phone ? () => setPicking(true) : undefined}
          onDragOver={(event) => {
            if (!event.dataTransfer.types.includes(DRAG_TYPE)) return
            event.preventDefault()
            setOver('new')
          }}
          onDragLeave={() => setOver((was) => (was === 'new' ? null : was))}
          onDrop={(event) => {
            event.preventDefault()
            const visitId = event.dataTransfer.getData(DRAG_TYPE)
            if (visitId) void dropAsNewPlan(visitId)
          }}
          className={cn('w-full rounded-[14px] border-[1.5px] border-dashed p-[15px] text-center text-[13px]',
            over === 'new'
              ? 'border-accent-line bg-accent-soft text-accent'
              : 'border-line-strong text-text-muted hover:text-accent')}
        >
          {phone ? BILLING_TAB.newPlan : DROP_FOR_NEW_PLAN}
        </button>
      )}
      {phone && canEdit && (
        <NewPlanMenu
          open={picking}
          anchor={null}
          onClose={() => setPicking(false)}
          onPick={(kind) => void onMutate('/plans', { method: 'POST', body: { kind } })}
        />
      )}
    </div>
  )
}
